import { z } from "zod";
import { FRIEND_DIRECTIONS, FRIEND_RELATIONS } from "../domain/social";

/**
 * A user paired with the caller's relationship to them. This is the single unit
 * of every friends-related response: the accepted-friends list, the incoming and
 * outgoing pending lists, user search hits, and the result of every mutation.
 *
 * It is an OTHER user's view, so — like `PublicUser` — it NEVER carries the private
 * Telegram *identifiers* (`telegramId` / Telegram `username`); those stay with the
 * owner (see `SelfProfile`). It DOES carry the Telegram display **name**
 * (`firstName` / `lastName`) — that is not private and is the fallback label when a
 * user has set no in-game `nickname`, so rows never degrade to a bare "(کاربر)".
 * `userId` (the other user's app id, a cuid) identifies the row and is the id
 * everything keys on: the friends API, presence, and the DM conversation the client
 * opens. The public handle is `appUsername`; the real uploaded photo resolves via
 * `avatarPhotoId` (falling back to the preset `avatarId`). `status` + `direction`
 * describe the relationship from the caller's perspective (see `@bing/shared` docs).
 */
export const friendViewSchema = z.object({
  userId: z.string(),
  /** Editable public handle (Telegram-style), or null if never set. */
  appUsername: z.string().nullable(),
  /** In-game display name, or null if the user hasn't set one. */
  nickname: z.string().nullable(),
  /** Telegram first name — NOT private; the display-name fallback when no nickname. */
  firstName: z.string().nullable(),
  /** Telegram last name — NOT private; joined with `firstName` for the fallback name. */
  lastName: z.string().nullable(),
  /** Preset avatar id (fallback when no photo is uploaded). */
  avatarId: z.string(),
  /** Uploaded avatar ref, or null — resolved via the authed `GET /avatar/:id`. */
  avatarPhotoId: z.string().nullable(),
  /** Official/verified account (system + staff identities) → renders a badge. */
  isOfficial: z.boolean().default(false),
  status: z.enum(FRIEND_RELATIONS),
  direction: z.enum(FRIEND_DIRECTIONS),
  /**
   * Whether the CALLER follows this user. Follows are one-directional and
   * independent of the friendship state machine (`status`/`direction`), except
   * that a block in either direction forbids following and removes the pair's
   * follows. Rides every FriendView row (friends lists, search hits, mutation
   * results) so the UI can render Follow/Unfollow per row.
   */
  following: z.boolean(),
});
export type FriendView = z.infer<typeof friendViewSchema>;

/** A list of friend views — friends, incoming pending, outgoing pending, or search hits. */
export const friendListSchema = z.array(friendViewSchema);
export type FriendList = z.infer<typeof friendListSchema>;

/**
 * `GET /friends/all` — all three friend lists in ONE response.
 *
 * The client needs the three together (the Friends tab renders all of them, and the nav badge
 * counts `incoming`), and it re-fetched them on a ~20s poll as three separate authenticated
 * requests. MEASURED on prod: `/friends` + `/friends/incoming` + `/friends/outgoing` were
 * **54.3% of every api request** (89,523 of 164,813 over a 3h40m daytime window) — three
 * near-identical numbers, which is the signature of one poll fanned out three ways.
 *
 * The three separate endpoints REMAIN, unchanged: a Mini App caches its bundle, so clients on
 * the old JS keep polling them long after this ships.
 */
export const friendListsSchema = z.object({
  friends: friendListSchema,
  incoming: friendListSchema,
  outgoing: friendListSchema,
});
export type FriendLists = z.infer<typeof friendListsSchema>;

/**
 * `GET /follows/counts` — the caller's follow totals. `following` = rows in
 * `GET /follows` (who I follow); `followers` = rows in `GET /follows/followers`
 * (who follows me). Split from the list endpoints so a profile header can show
 * numbers without pulling either list.
 */
export const followCountsSchema = z.object({
  followers: z.number().int().nonnegative(),
  following: z.number().int().nonnegative(),
});
export type FollowCounts = z.infer<typeof followCountsSchema>;
